import { FocusEvent, FocusEventHandler, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useController,
  useFormContext,
  UseControllerProps,
} from 'react-hook-form';
import { transformValidationOptions } from '../../../helpers/shared/transformValidationOptions';
import Patterns from '../../../../../utils/pattern/patterns';

export interface UsePhoneFieldControllerProps<TFieldValues>
  extends UseControllerProps<TFieldValues> {
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onChange?: (value?: string) => void;
  mobile?: boolean;
}

export function usePhoneFieldController<TFieldValues>(
  props: UsePhoneFieldControllerProps<TFieldValues>,
) {
  const {
    name,
    onBlur,
    onChange,
    rules,
    mobile,
    ...phoneFieldControllerProps
  } = props;

  const { t } = useTranslation();
  const { control, getValues } = useFormContext<TFieldValues>();

  const phoneFieldRules = transformValidationOptions<TFieldValues>(
    t,
    rules,
    getValues,
  );
  const required = typeof phoneFieldRules.required !== 'undefined';

  phoneFieldRules.validate = {
    isPhone(value) {
      const isValid = mobile
        ? Patterns.isMobileNumber(value)
        : Patterns.isTelephoneNumber(value);

      if (value && !isValid) {
        return t('site.pricing.validation.error.code.INVALID_PHONE_NUMBER');
      }
    },
    ...phoneFieldRules.validate,
  };

  const controllerProps = useController<TFieldValues>({
    name,
    control,
    rules: phoneFieldRules,
    ...phoneFieldControllerProps,
  });
  const { field } = controllerProps;

  const handleChange = useCallback(
    (value?: string) => {
      field.onChange(value);

      if (onChange) {
        onChange(value);
      }
    },
    [field, onChange],
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      field.onBlur();

      if (onBlur) {
        onBlur(event);
      }
    },
    [field, onBlur],
  );

  return {
    required,
    onChangeField: handleChange,
    onBlurField: handleBlur,
    ...controllerProps,
  };
}
